import { APP_CONFIG } from "../config";
import { ApiError } from "../interfaces";

const SUPPORTED_LANGUAGES = ["en", "uk", "ru"];

// Endpoints that must never trigger a token refresh: a 401 here means the
// credentials themselves are bad, not that the access token expired.
const NO_REFRESH_ENDPOINTS = ["/auth/refresh", "/auth/login"];

const getCurrentLang = (): string => {
  const urlLang = window.location.pathname.split("/")[1];
  if (SUPPORTED_LANGUAGES.includes(urlLang)) {
    return urlLang;
  }
  const savedLang = localStorage.getItem("i18nextLng") || "";
  return SUPPORTED_LANGUAGES.includes(savedLang) ? savedLang : "en";
};

export const redirectToLogin = () => {
  window.location.href = `/${getCurrentLang()}/login`;
};

// Single-flight refresh: concurrent 401s share one refresh request.
let refreshPromise: Promise<boolean> | null = null;

const refreshSession = (): Promise<boolean> => {
  if (!refreshPromise) {
    refreshPromise = fetch(`${APP_CONFIG.API_BASE_URL}/auth/refresh`, {
      method: "POST",
      credentials: "include",
    })
      .then((response) => response.ok)
      .catch(() => false)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

async function buildError(response: Response): Promise<ApiError> {
  const errorBody = await response.json().catch(() => ({}));
  return {
    message: errorBody.message || "An error occurred during the request",
    status: response.status,
    body: errorBody,
  };
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {},
  hasRetried = false,
  isBlob = false, // <-- Новий параметр
): Promise<T> {
  const url = `${APP_CONFIG.API_BASE_URL}${endpoint}`;
  const headers = new Headers(options.headers || {});

  // Якщо це FormData, НЕ ставимо Content-Type, браузер зробить це сам
  if (options.body != null && !(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  // Extract XSRF-TOKEN from cookies
  let match = document.cookie.match(new RegExp("(^| )XSRF-TOKEN=([^;]+)"));

  // Якщо токена немає, але це POST/PUT/DELETE запит — робимо швидкий GET запит, щоб бекенд видав нам куку
  if (
    !match &&
    options.method &&
    ["POST", "PUT", "PATCH", "DELETE"].includes(options.method.toUpperCase())
  ) {
    await fetch(`${APP_CONFIG.API_BASE_URL}/auth/me`, {
      credentials: "include",
    });
    match = document.cookie.match(new RegExp("(^| )XSRF-TOKEN=([^;]+)"));
  }

  if (match) {
    headers.set("X-XSRF-TOKEN", decodeURIComponent(match[2]));
  }

  const fetchOptions: RequestInit = {
    ...options,
    headers,
    credentials: "include",
  };
  const response = await fetch(url, fetchOptions);

  if (
    response.status === 401 &&
    !hasRetried &&
    !NO_REFRESH_ENDPOINTS.some((path) => endpoint.startsWith(path))
  ) {
    const refreshed = await refreshSession();
    if (refreshed) {
      return request<T>(endpoint, options, true);
    }
    // Session is gone. /auth/me is exempt from the redirect: it runs on every
    // page load (including public pages), and anonymous visitors must not be
    // bounced to the login form — ProtectedRoute handles private routes.
    if (!endpoint.startsWith("/auth/me")) {
      redirectToLogin();
    }
    throw await buildError(response);
  }

  if (!response.ok) {
    throw await buildError(response);
  }

  if (response.status === 204) return {} as T;

  if (isBlob) {
    return (await response.blob()) as unknown as T;
  }

  const text = await response.text();
  return text ? JSON.parse(text) : ({} as T);
}

/**
 * A form upload that reports how much has been sent. fetch() cannot, and a large upload (an own
 * landing may be 150 MB) looks frozen without it, so this one goes through XMLHttpRequest with
 * the same CSRF header and one refresh-and-retry on 401 as request().
 */
async function uploadWithProgress<T>(
  endpoint: string,
  formData: FormData,
  onProgress: (percent: number) => void,
  hasRetried = false,
): Promise<T> {
  let match = document.cookie.match(new RegExp("(^| )XSRF-TOKEN=([^;]+)"));
  if (!match) {
    await fetch(`${APP_CONFIG.API_BASE_URL}/auth/me`, {
      credentials: "include",
    });
    match = document.cookie.match(new RegExp("(^| )XSRF-TOKEN=([^;]+)"));
  }

  const { status, text } = await new Promise<{ status: number; text: string }>(
    (resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${APP_CONFIG.API_BASE_URL}${endpoint}`);
      xhr.withCredentials = true;
      if (match) {
        xhr.setRequestHeader("X-XSRF-TOKEN", decodeURIComponent(match[2]));
      }
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
      xhr.onload = () =>
        resolve({ status: xhr.status, text: xhr.responseText });
      xhr.onerror = () =>
        reject({
          message:
            "The upload was interrupted. Check your connection and try again.",
          status: 0,
        });
      xhr.send(formData);
    },
  );

  if (status === 401 && !hasRetried && (await refreshSession())) {
    onProgress(0);
    return uploadWithProgress<T>(endpoint, formData, onProgress, true);
  }
  if (status === 401) {
    redirectToLogin();
  }
  if (status < 200 || status >= 300) {
    let body: { message?: string } = {};
    try {
      body = JSON.parse(text);
    } catch {
      // Not JSON: a proxy's error page.
    }
    const error: ApiError = {
      message: body.message || "An error occurred during the request",
      status,
      body,
    };
    throw error;
  }
  return text ? JSON.parse(text) : ({} as T);
}

export const apiClient = {
  get: <T>(endpoint: string, options?: RequestInit) =>
    request<T>(endpoint, { ...options, method: "GET" }),

  post: <T>(endpoint: string, body: unknown, options?: RequestInit) =>
    request<T>(endpoint, {
      ...options,
      method: "POST",
      body: JSON.stringify(body),
    }),

  put: <T>(endpoint: string, body: unknown, options?: RequestInit) =>
    request<T>(endpoint, {
      ...options,
      method: "PUT",
      body: JSON.stringify(body),
    }),

  patch: <T>(endpoint: string, body: unknown, options?: RequestInit) =>
    request<T>(endpoint, {
      ...options,
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  delete: <T>(endpoint: string, options?: RequestInit) =>
    request<T>(endpoint, { ...options, method: "DELETE" }),
  postForm: <T>(endpoint: string, formData: FormData) =>
    request<T>(endpoint, { method: "POST", body: formData }),
  postFormWithProgress: <T>(
    endpoint: string,
    formData: FormData,
    onProgress: (percent: number) => void,
  ) => uploadWithProgress<T>(endpoint, formData, onProgress),
  download: (endpoint: string) =>
    request<Blob>(endpoint, { method: "GET" }, false, true),
};
